// Shared timezone helpers for the "save UTC, render in the viewer's own
// timezone" flows: Create Service Request, Transaction Timeline "Add to
// Calendar", and the Calendar page "Add Event". See CalendarEvent.timeZone
// (rialty-calendar-service) for the matching backend field.
//
// There is no standalone timezone preference — a viewer's timezone is always
// derived from their profile's State field (Licensed Address state for a
// verified Agent/Broker, Personal Address state otherwise; UserProfile.state
// on the backend already resolves this fallback at write time, so the same
// `state` value read here is authoritative). STATE_TO_ZONE below is a direct
// port of rialty-calendar-service's StateTimezoneResolver.java (and
// transaction-service's own duplicate of the same table) so the client's
// rendering agrees with the backend's own deadline/reminder zone resolution.
//
// Nothing here changes how any *existing* record (no timeZone marker) is
// read or displayed — every function below falls back to the exact
// pre-existing raw-string behavior when the marker is absent, so records
// created before this feature existed (and anything created by a client that
// doesn't send the marker, e.g. the mobile apps) are unaffected.

const DEFAULT_ZONE = 'America/Los_Angeles';

// Multi-zone states resolve to the zone covering the largest population
// centre (e.g. Texas -> Central, Idaho -> Mountain, Florida -> Eastern),
// mirroring StateTimezoneResolver.java exactly.
const STATE_TO_ZONE = {
  // Eastern
  CT: 'America/New_York', DE: 'America/New_York', FL: 'America/New_York',
  GA: 'America/New_York', IN: 'America/New_York', KY: 'America/New_York',
  ME: 'America/New_York', MD: 'America/New_York', MA: 'America/New_York',
  MI: 'America/New_York', NH: 'America/New_York', NJ: 'America/New_York',
  NY: 'America/New_York', NC: 'America/New_York', OH: 'America/New_York',
  PA: 'America/New_York', RI: 'America/New_York', SC: 'America/New_York',
  VT: 'America/New_York', VA: 'America/New_York', WV: 'America/New_York',
  DC: 'America/New_York',
  // Central
  AL: 'America/Chicago', AR: 'America/Chicago', IL: 'America/Chicago',
  IA: 'America/Chicago', KS: 'America/Chicago', LA: 'America/Chicago',
  MN: 'America/Chicago', MS: 'America/Chicago', MO: 'America/Chicago',
  NE: 'America/Chicago', ND: 'America/Chicago', OK: 'America/Chicago',
  SD: 'America/Chicago', TN: 'America/Chicago', TX: 'America/Chicago',
  WI: 'America/Chicago',
  // Mountain
  CO: 'America/Denver', ID: 'America/Denver', MT: 'America/Denver',
  NM: 'America/Denver', UT: 'America/Denver', WY: 'America/Denver',
  // Arizona (no DST)
  AZ: 'America/Phoenix',
  // Pacific
  CA: 'America/Los_Angeles', NV: 'America/Los_Angeles', OR: 'America/Los_Angeles',
  WA: 'America/Los_Angeles',
  // Alaska / Hawaii (Hawaii: no DST)
  AK: 'America/Anchorage',
  HI: 'Pacific/Honolulu',
};

/** Resolves a two-letter US state code to its primary IANA zone, falling
 * back to DEFAULT_ZONE for null/blank/unrecognized input — same contract as
 * StateTimezoneResolver.resolveOrDefault on the backend. */
export function resolveTimezoneFromState(stateCode) {
  if (!stateCode) return DEFAULT_ZONE;
  const key = String(stateCode).trim().toUpperCase();
  return STATE_TO_ZONE[key] || DEFAULT_ZONE;
}

// Reverse of STATE_TO_ZONE for the signup pre-fill: one representative US
// state per IANA zone, picked by the largest-population state in each zone's
// bucket (CA for Pacific, TX for Central, NY for Eastern, CO for Mountain).
// Zones that cover one state only (AZ, AK, HI) map one-to-one. This is only
// used as a *sensible default* at initial signup — the user still edits the
// state field before submit, and all post-signup rendering stays driven by
// the stored profile state, so a miss here is self-correcting.
const ZONE_TO_FALLBACK_STATE = {
  'America/New_York': 'NY',
  'America/Chicago': 'TX',
  'America/Denver': 'CO',
  'America/Los_Angeles': 'CA',
  'America/Phoenix': 'AZ',
  'America/Anchorage': 'AK',
  'Pacific/Honolulu': 'HI',
};

/**
 * One-time signup helper: reads the browser's own IANA timezone and returns a
 * two-letter US state code that falls in that zone, so the signup form's state
 * picker can land on something sensible instead of blank. Returns "" when the
 * device zone isn't a US zone we know, when `Intl` is unavailable, or anything
 * else goes wrong — the caller treats that as "leave the picker empty".
 *
 * Device detection is deliberately NOT used anywhere else: once the user picks
 * a state (or accepts the pre-fill) it's persisted on their profile, and every
 * subsequent timezone decision reads from `UserProfile.state`, never from the
 * device clock. This function exists only to make the first signup kinder.
 */
export function getDefaultStateFromDeviceZone() {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return ZONE_TO_FALLBACK_STATE[zone] || '';
  } catch {
    return '';
  }
}

/**
 * The viewing user's own timezone, derived from their profile's State field
 * (Licensed Address state, falling back to Personal Address state — see
 * header comment). Reads the same `localStorage.user` blob every other page
 * in this app already uses for the logged-in user.
 *
 * `state` is meant to already carry the backend-resolved value (Licensed
 * Address state for a verified Agent/Broker, Personal Address otherwise —
 * UserProfile.state resolves this server-side). In practice some cached
 * `user` blobs have `state`/`personalState` unset while `licenseState` (the
 * Licensed Address state, surfaced separately on the profile page) is
 * populated, so it's checked too before falling back to the default zone.
 */
export function getViewerTimezone() {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    const state = (stored && (stored.state || stored.personalState || stored.licenseState)) || null;
    return resolveTimezoneFromState(state);
  } catch {
    return DEFAULT_ZONE;
  }
}

// Returns how far a zone's wall-clock reads ahead of UTC (in ms) at the given
// instant — e.g. +/- the zone's current UTC offset, DST included. Used to
// convert a wall-clock date/time *in that zone* to the UTC instant it
// represents, since JS's Date has no built-in "parse this as zone X" mode.
function zoneOffsetMs(date, zone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = {};
  dtf.formatToParts(date).forEach((p) => { if (p.type !== 'literal') parts[p.type] = p.value; });
  const asUtc = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour), Number(parts.minute), Number(parts.second),
  );
  return asUtc - date.getTime();
}

// Converts wall-clock y/m/d/hh/mm *as read in `zone`* to the UTC instant
// (epoch ms) it represents. Single-pass correction (guess as if UTC, measure
// the zone's offset at that instant, correct once) — matches every other
// zone-conversion helper in this file in not handling the ambiguous/skipped
// hour that exists during a DST transition, an accepted, documented edge case
// here just like elsewhere in this module.
function zonedWallClockToUtcMs(y, m, d, hh, mm, zone) {
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  return guess - zoneOffsetMs(new Date(guess), zone);
}

/**
 * Converts a local date-picker value ("YYYY-MM-DD") and time-picker value
 * ("HH:mm", optional) into their UTC-equivalent wall-clock strings, in the
 * exact same "YYYY-MM-DD"/"HH:mm" shape the calendar-service API already
 * validates — so the request payload shape never changes, only the values.
 * Pair with `timeZone: "UTC"` in the same request so readers know the
 * strings are UTC-encoded, not local-encoded.
 *
 * The picker values are interpreted as wall-clock time *in `zone`* (default:
 * the viewer's own profile timezone) — not the device's timezone. This is
 * the fix for the "device clock/timezone disagrees with profile timezone"
 * gap: whatever the user sees and types in the picker is what gets saved,
 * regardless of what timezone their computer happens to be set to.
 */
export function toUtcWallClock(localDateStr, localTimeStr, zone = getViewerTimezone()) {
  if (!localDateStr) return { scheduledDate: localDateStr, scheduledTime: localTimeStr };
  const timePart = localTimeStr || '00:00';
  const [y, m, d] = localDateStr.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if (![y, m, d, hh, mm].every(Number.isFinite)) {
    return { scheduledDate: localDateStr, scheduledTime: localTimeStr };
  }
  let instant;
  try {
    instant = new Date(zonedWallClockToUtcMs(y, m, d, hh, mm, zone));
  } catch {
    // Zone resolution failed (e.g. unsupported Intl environment) — fall back
    // to the previous device-timezone behavior rather than throwing.
    instant = new Date(`${localDateStr}T${timePart}:00`);
  }
  if (Number.isNaN(instant.getTime())) {
    return { scheduledDate: localDateStr, scheduledTime: localTimeStr };
  }
  const pad = (n) => String(n).padStart(2, '0');
  const scheduledDate = `${instant.getUTCFullYear()}-${pad(instant.getUTCMonth() + 1)}-${pad(instant.getUTCDate())}`;
  const scheduledTime = localTimeStr ? `${pad(instant.getUTCHours())}:${pad(instant.getUTCMinutes())}` : localTimeStr;
  return { scheduledDate, scheduledTime };
}

/**
 * Same profile-timezone interpretation as toUtcWallClock, but returns a
 * single ISO-8601 UTC instant string instead of a scheduledDate/scheduledTime
 * pair — the shape ServiceRequest.requestedDate/requestedTime and other
 * OffsetDateTime-backed fields expect. Returns null for a blank date (mirrors
 * those fields' optionality).
 */
export function toUtcInstant(localDateStr, localTimeStr, zone = getViewerTimezone()) {
  if (!localDateStr) return null;
  const timePart = localTimeStr || '00:00';
  const [y, m, d] = localDateStr.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if (![y, m, d, hh, mm].every(Number.isFinite)) return null;
  let ms;
  try {
    ms = zonedWallClockToUtcMs(y, m, d, hh, mm, zone);
  } catch {
    ms = new Date(`${localDateStr}T${timePart}:00`).getTime();
  }
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toISOString();
}

/**
 * The current date/time, as wall-clock values in `zone` (default: the
 * viewer's own profile timezone) — for pre-filling a date/time picker with
 * "now" correctly. Deliberately does NOT read `Date.prototype`'s local
 * getters (getHours/toTimeString/etc.) or toISOString() — both are always
 * relative to the device's own clock/timezone, which is exactly the "silent
 * wrong default" bug this exists to avoid.
 */
export function nowWallClockInViewerZone(zone = getViewerTimezone()) {
  const now = new Date();
  try {
    const dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone: zone, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
    const parts = {};
    dtf.formatToParts(now).forEach((p) => { if (p.type !== 'literal') parts[p.type] = p.value; });
    return {
      localDate: `${parts.year}-${parts.month}-${parts.day}`,
      localTime: `${parts.hour}:${parts.minute}`,
    };
  } catch {
    // Zone resolution failed — fall back to device-local "now" rather than throwing.
    const pad = (n) => String(n).padStart(2, '0');
    return {
      localDate: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
      localTime: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
    };
  }
}

const ZONE_ABBREV_CACHE = new Map();

function zoneAbbreviation(zone, date) {
  const cacheKey = zone;
  let fmt = ZONE_ABBREV_CACHE.get(cacheKey);
  if (!fmt) {
    try {
      fmt = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' });
      ZONE_ABBREV_CACHE.set(cacheKey, fmt);
    } catch {
      return '';
    }
  }
  const part = fmt.formatToParts(date).find((p) => p.type === 'timeZoneName');
  return part ? part.value : '';
}

/**
 * The "YYYY-MM-DD" the event falls on for the given viewer — used for
 * day-bucketing calendar grids/lists. When `event.timeZone` is present, the
 * UTC-encoded scheduledDate/scheduledTime can land on a different calendar
 * day in the viewer's zone than the raw digits suggest (e.g. a UTC-encoded
 * late-night event can fall on the *next* local day for a viewer east of
 * UTC), so this re-derives the day from the real instant. Falls back to the
 * raw scheduledDate string when there's no marker, matching today's
 * behavior exactly for every pre-existing event.
 */
export function eventLocalDateKey(event, viewerTimezone) {
  const rawDate = event?.scheduledDate;
  if (!rawDate) return rawDate;
  // Naive/literal path when either the timezone marker is missing (legacy)
  // or the event is date-only (no scheduledTime). A date-only event has no
  // real time-of-day to convert; localizing "midnight UTC" would shift the
  // bucket by a day for viewers whose zone straddles it. Mirrors the guard
  // in formatEventForViewer so display + bucketing stay consistent.
  if (!event.timeZone || !event.scheduledTime) return rawDate;

  const [y, m, d] = rawDate.split('-').map(Number);
  const [hh, mm] = event.scheduledTime.split(':').map(Number);
  const instant = new Date(Date.UTC(y, (m || 1) - 1, d || 1, hh || 0, mm || 0));
  const zone = viewerTimezone || getViewerTimezone();
  // en-CA formats as YYYY-MM-DD, matching the raw field's own shape.
  return instant.toLocaleDateString('en-CA', { timeZone: zone });
}

/**
 * Renders a calendar-event-shaped record ({ scheduledDate, scheduledTime,
 * timeZone, allDay }) for the given viewer. When `timeZone` is present
 * (currently only ever "UTC"), converts to the viewer's zone and appends a
 * short indicator (e.g. "9:30 AM PT"). When absent — every event that
 * existed before this feature, and anything created by a path that doesn't
 * send the marker — falls back to the plain raw-string display used
 * everywhere in this app today, unchanged.
 */
export function formatEventForViewer(event, viewerTimezone) {
  const rawDate = event?.scheduledDate;
  const rawTime = event?.scheduledTime;
  if (!rawDate) return { dateLabel: '', timeLabel: '' };

  // Naive/literal render when either:
  //   (1) no timeZone marker — the legacy pre-migration path, or
  //   (2) timeZone is present but scheduledTime is null/empty — a date-only
  //       event has no meaningful time-of-day to convert; treating the
  //       stored date as midnight-UTC and localizing it would shift the
  //       visible date by up to a day for viewers whose zone straddles the
  //       stored date's UTC midnight (e.g. `2026-09-20` renders as "Sep 19"
  //       for a PDT viewer). Date-only events should always show their
  //       literal scheduledDate — the wall-clock day the requester picked.
  if (!event.timeZone || !rawTime) {
    const [y, m, d] = rawDate.split('-').map(Number);
    const dateLabel = Number.isFinite(y)
      ? new Date(y, (m || 1) - 1, d || 1).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : rawDate;
    let timeLabel = '';
    if (rawTime) {
      const [hh, mm] = rawTime.split(':').map(Number);
      if (Number.isFinite(hh)) {
        const suffix = hh >= 12 ? 'PM' : 'AM';
        const hour12 = hh % 12 === 0 ? 12 : hh % 12;
        timeLabel = `${hour12}:${String(mm || 0).padStart(2, '0')} ${suffix}`;
      } else {
        timeLabel = rawTime;
      }
    }
    return { dateLabel, timeLabel };
  }

  // event.scheduledDate/scheduledTime are UTC wall-clock — build the real instant.
  const utcMs = Date.UTC(
    ...(() => {
      const [y, m, d] = rawDate.split('-').map(Number);
      const [hh, mm] = (rawTime || '00:00').split(':').map(Number);
      return [y, (m || 1) - 1, d || 1, hh || 0, mm || 0];
    })(),
  );
  const instant = new Date(utcMs);
  const zone = viewerTimezone || getViewerTimezone();

  const dateLabel = instant.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: zone });
  let timeLabel = '';
  if (rawTime) {
    const timeStr = instant.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: zone });
    const abbrev = zoneAbbreviation(zone, instant);
    timeLabel = abbrev ? `${timeStr} ${abbrev}` : timeStr;
  }
  return { dateLabel, timeLabel };
}

/**
 * Splits an ISO instant into its wall-clock <input type="date"> and
 * <input type="time"> values as read in `zone` (default: the viewer's own
 * profile timezone). Pair with `toUtcInstant` to round-trip a pickable
 * edit form without shifting the underlying instant — the read row and
 * the picker land on the same wall-clock as the viewer's profile zone.
 */
export function instantToWallClockInZone(isoString, zone = getViewerTimezone()) {
  if (!isoString) return { localDate: '', localTime: '' };
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return { localDate: '', localTime: '' };
  try {
    const dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone: zone, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
    const parts = {};
    dtf.formatToParts(d).forEach((p) => { if (p.type !== 'literal') parts[p.type] = p.value; });
    return {
      localDate: `${parts.year}-${parts.month}-${parts.day}`,
      localTime: `${parts.hour}:${parts.minute}`,
    };
  } catch {
    // Zone resolution failed — fall back to device zone (legacy behavior).
    const pad = (n) => String(n).padStart(2, '0');
    return {
      localDate: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      localTime: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    };
  }
}

/**
 * Same idea as formatEventForViewer, but for fields that are already real
 * ISO instants (e.g. ServiceRequest.requestedDate/requestedTime — always
 * UTC ISO strings end to end, unlike calendar events). Always converts,
 * since these fields carry no ambiguity to fall back on.
 */
export function formatInstantForViewer(isoString, viewerTimezone) {
  if (!isoString) return { dateLabel: '', timeLabel: '' };
  const instant = new Date(isoString);
  if (Number.isNaN(instant.getTime())) return { dateLabel: '', timeLabel: '' };
  const zone = viewerTimezone || getViewerTimezone();
  const dateLabel = instant.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: zone });
  const timeStr = instant.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: zone });
  const abbrev = zoneAbbreviation(zone, instant);
  const timeLabel = abbrev ? `${timeStr} ${abbrev}` : timeStr;
  return { dateLabel, timeLabel };
}

/**
 * Same as formatInstantForViewer, but for backend fields serialized as a bare
 * "YYYY-MM-DDTHH:mm:ss[.SSS]" string with no zone/offset marker (Java
 * LocalDateTime's default JSON shape — e.g. Transaction.createdAt) whose
 * underlying value the backend guarantees is already UTC. A marker-less
 * string is otherwise parsed by `new Date()` as browser-local time per the
 * ECMA-262 Date Time String Format, which silently misreads it — this
 * appends the missing 'Z' first so it's read as the UTC instant it actually
 * represents, then converts to the viewer's own zone same as
 * formatInstantForViewer. No-ops (passes through unchanged) for a string
 * that already carries a 'Z' or an explicit offset.
 */
export function formatUtcNaiveInstantForViewer(naiveIsoString, viewerTimezone) {
  if (!naiveIsoString) return { dateLabel: '', timeLabel: '' };
  const hasZone = /[Zz]|[+-]\d{2}:\d{2}$/.test(naiveIsoString);
  const isoWithZone = hasZone ? naiveIsoString : `${naiveIsoString}Z`;
  return formatInstantForViewer(isoWithZone, viewerTimezone);
}

/**
 * Format a plain calendar date (Java LocalDate, serialized "YYYY-MM-DD" — e.g.
 * Milestone.dueDate/completedDate, Transaction.expectedClosingDate) exactly as
 * stored, e.g. "Oct 6, 2026". `new Date("YYYY-MM-DD")` reads a date-only string
 * as UTC midnight, which every zone west of UTC renders as the previous day;
 * building the Date from its parts at local midnight avoids any zone math.
 * Anything that isn't a bare date (a full ISO timestamp) is formatted as before.
 */
export function formatPlainDate(value, viewerTimezone) {
  if (!value) return '';
  const match = typeof value === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (match) {
    // Bare YYYY-MM-DD — a calendar day with no time-of-day. Rendering it
    // through any zone would risk a day-shift (e.g. a Sep 20 closing date
    // becoming Sep 19 for a Pacific viewer if parsed as midnight UTC). Build
    // the Date at local midnight from its parts and format literally — no
    // zone math.
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  // Full ISO instant (carries a time + zone marker) — render in the viewer's
  // profile zone, not the device zone.
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const zone = viewerTimezone || getViewerTimezone();
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: zone });
}
